import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";

// ── DB row shape — matches holdback_ledger_entries table ──────────────
interface HoldbackLedgerEntryRow {
  id: string;
  holdback_id: string;
  job_id: string;
  contractor_id: string;
  action: string;
  amount: number;
  reason: string;
  actor_id: string;
  actor_role: string;
  created_at: string;
  source_callback_id: string | null;
  reverses_entry_id: string | null;
  paused_by_dispute_id: string | null;
  enforcement_mode_at_write: string;
  organization_id: string;
}

// ── Mappers ───────────────────────────────────────────────────────────
function toUiEntry(row: HoldbackLedgerEntryRow): HoldbackLedgerEntry {
  return {
    id: row.id,
    holdbackId: row.holdback_id,
    jobId: row.job_id,
    contractorId: row.contractor_id,
    action: row.action as HoldbackLedgerEntry["action"],
    amount: row.amount,
    reason: row.reason,
    actorId: row.actor_id,
    actorRole: row.actor_role,
    createdAt: row.created_at,
    sourceCallbackId: row.source_callback_id ?? undefined,
    reversesEntryId: row.reverses_entry_id ?? undefined,
    pausedByDisputeId: row.paused_by_dispute_id ?? undefined,
    enforcementModeAtWrite: row.enforcement_mode_at_write as HoldbackLedgerEntry["enforcementModeAtWrite"],
  };
}

function toDbRow(
  entry: HoldbackLedgerEntry,
  organizationId: string | null,
): Omit<HoldbackLedgerEntryRow, "created_at"> & { created_at?: string } {
  return {
    id: entry.id,
    holdback_id: entry.holdbackId,
    job_id: entry.jobId,
    contractor_id: entry.contractorId,
    action: entry.action,
    amount: entry.amount,
    reason: entry.reason,
    actor_id: entry.actorId,
    actor_role: entry.actorRole,
    created_at: entry.createdAt,
    source_callback_id: entry.sourceCallbackId ?? null,
    reverses_entry_id: entry.reversesEntryId ?? null,
    paused_by_dispute_id: entry.pausedByDisputeId ?? null,
    enforcement_mode_at_write: entry.enforcementModeAtWrite,
    organization_id: organizationId as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────
export const HoldbackLedgerService = {
  async getAll(): Promise<{ data: HoldbackLedgerEntry[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("holdback_ledger_entries")
      .select("*")
      .order("created_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as HoldbackLedgerEntryRow[]).map(toUiEntry), error: null };
  },

  async getByHoldbackId(holdbackId: string): Promise<{ data: HoldbackLedgerEntry[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("holdback_ledger_entries")
      .select("*")
      .eq("holdback_id", holdbackId)
      .order("created_at", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as HoldbackLedgerEntryRow[]).map(toUiEntry), error: null };
  },

  async append(entry: HoldbackLedgerEntry): Promise<{ data: HoldbackLedgerEntry | null; error: unknown }> {
    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = toDbRow(entry, organizationId);

    const { data, error } = await supabase
      .from("holdback_ledger_entries")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[HoldbackLedgerService.append] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiEntry(data as HoldbackLedgerEntryRow), error: null };
  },
};
